import { Container, Row, Col, Button } from 'react-bootstrap';
import Link from 'next/link';
import { TextFormInput, TextAreaFormInput } from '@/components';
import InquiryFormExtras from '@/components/InquiryFormExtras';
import useSiteInquiryForm from '@/hooks/useSiteInquiryForm';
import { getHomeIcon } from './homeIconMap';
import { getPartnerInquiryPath, getPartnerLoginPath } from '@/utils/partnerRoutes';

const defaultPartnerItems = [
  { icon: 'FaHandshake', title: 'Exclusive Rates' },
  { icon: 'FaChartLine', title: 'Real-time Data' },
  { icon: 'FaUsers', title: 'Dedicated Support' }
];

const PartnerSection = ({ sectionData = null }) => {
  const {
    control,
    onSubmit,
    status,
    error,
    confirmation,
    submitting,
    resetStatus,
    websiteField,
    setWebsiteField,
    turnstile,
    showTurnstileConfigWarning,
  } = useSiteInquiryForm({
    formType: 'partner',
    action: 'partner_home_inquiry',
    defaultValues: {
      companyName: '',
      name: '',
      email: '',
      phone: '',
      message: ''
    },
    mapValues: (values) => ({
      company_name: values.companyName,
      contact_name: values.name,
      email: values.email,
      phone: values.phone,
      message: values.message,
    }),
  });
  const titleText = sectionData?.title?.trim() ? sectionData.title : 'Partner With Us';
  const introText = sectionData?.intro_text ??
    "Join our partner network and offer your customers premium travel experiences in Crete. Access exclusive rates, real-time availability, and dedicated support.";
  const rawPartnerItems = Array.isArray(sectionData?.items) ? sectionData.items : [];
  const activePartnerItems = rawPartnerItems.filter((item) => {
    if (!item) return false;
    return item.is_active === 1 || item.is_active === "1" || item.is_active === true || item.is_active === undefined;
  });
  const partnerItems = activePartnerItems.length > 0 ? activePartnerItems : defaultPartnerItems;

  const bgColor = sectionData?.content?.background_color || sectionData?.background_color || 'light';
  const bgEffect = sectionData?.content?.background_effect || sectionData?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  const hasPatternEffect = bgColor === 'primary' && bgEffect === 'pattern';
  const useDbBackground = !!(sectionData?.content?.background_color || sectionData?.background_color);

  return (
    <section className={`partner-section py-5 py-lg-6 ${bgClass} ${hasDotsEffect || hasPatternEffect ? 'position-relative overflow-hidden' : ''} ${hasPatternEffect ? 'section-bg-pattern' : ''}`} {...(useDbBackground && { 'data-bg-from-db': 'true' })}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container>
        <Row className="mb-4">
          <Col lg={12} className="text-center">
            <h2 className="sec-title mb-3">{titleText}</h2>
            
            {/* Icons under title */}
            <Row className="g-4 justify-content-center mb-4">
              {partnerItems.map((item, index) => {
                const title = item?.title?.trim() ? item.title : '';
                if (!title) return null;
                const Icon = getHomeIcon(item?.icon);
                const iconColor = item?.icon_color?.trim() ? item.icon_color : null;
                return (
                  <Col xs={6} md={4} lg={3} key={item?.id ?? index}>
                    <div className="text-center">
                      <div className="partner-icon-wrapper mb-3">
                        {Icon && (
                          <Icon className={`partner-icon ${iconColor ? `text-${iconColor}` : ''}`.trim()} />
                        )}
                      </div>
                      <h5 className="mb-0">{title}</h5>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>

        <Row className="align-items-start">
          <Col lg={6}>
            <p className="lead mb-4">
              <span dangerouslySetInnerHTML={{ __html: introText }} />
            </p>
            <div className="d-flex flex-column flex-md-row gap-3">
              <Link href={getPartnerInquiryPath()} className="btn btn-primary btn-lg">
                Request access
              </Link>
              <Link href={getPartnerLoginPath()} className="btn btn-outline-primary btn-lg">
                Partner Login
              </Link>
            </div>
          </Col>
          <Col lg={6}>
            {status === 'success' ? (
              <InquiryFormExtras
                status={status}
                confirmation={confirmation}
                onReset={resetStatus}
              />
            ) : (
            <form onSubmit={onSubmit} className="partner-contact-form" noValidate>
              <div className="row g-3">
                <TextFormInput
                  name="companyName"
                  label="Company Name"
                  containerClass="col-md-6"
                  control={control}
                />
                <TextFormInput
                  name="phone"
                  label="Phone"
                  containerClass="col-md-6"
                  control={control}
                />
                <TextFormInput
                  name="name"
                  label="Your Name *"
                  containerClass="col-md-6"
                  control={control}
                  rules={{ required: true }}
                />
                <TextFormInput
                  name="email"
                  label="Email *"
                  containerClass="col-md-6"
                  control={control}
                  rules={{ required: true }}
                />
                <TextAreaFormInput
                  name="message"
                  label="Message"
                  containerClass="col-12"
                  rows={3}
                  control={control}
                />
                  <Col xs={12}>
                    <InquiryFormExtras
                      honeypotId="home-partner-website-field"
                      websiteField={websiteField}
                      setWebsiteField={setWebsiteField}
                      turnstileRef={turnstile.containerRef}
                      showTurnstileConfigWarning={showTurnstileConfigWarning}
                      error={error}
                      status={status}
                    />
                  </Col>
                  <Col xs={12}>
                    <Button variant="primary" type="submit" className="w-100" disabled={submitting}>
                      {submitting ? 'Sending…' : 'Contact Us'}
                    </Button>
              </Col>
                </div>
            </form>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default PartnerSection;

